// Users.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownRefs = useRef({});

  const [selectedUser, setSelectedUser] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // debounced value

  const [tierFilter, setTierFilter] = useState('all'); // all | basic | gold | pro
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive | suspended | flagged
  const [sortBy, setSortBy] = useState('lastActiveDesc'); // lastActiveDesc | createdDesc | nameAsc

  const [toast, setToast] = useState(null); // small inline toast
  const navigate = useNavigate();

  // --- Live users snapshot
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(list);
        setLoading(false);
      },
      (err) => {
        console.error('❌ users snapshot failed:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // --- Close row dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!Object.values(dropdownRefs.current).some((ref) => ref?.contains(e.target))) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Debounce search input → query
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 220);
    return () => clearTimeout(t);
  }, [searchInput]);

  // --- Utilities
  const logAdminAction = async (action, targetUserId, details = {}) => {
    try {
      await addDoc(collection(db, 'adminLogs'), {
        timestamp: serverTimestamp(),
        adminId: localStorage.getItem('adminUid') || 'admin_demo',
        action,
        targetUserId,
        details,
      });
    } catch (err) {
      console.error('❌ Log failed:', err);
    }
  };

  const coerceDate = (value) => {
    // Supports Firestore Timestamp, ISO string, millis, or Date
    try {
      if (!value) return null;
      if (value?.toDate) return value.toDate();
      if (value instanceof Date) return value;
      if (typeof value === 'number') return new Date(value);
      if (typeof value === 'string') return new Date(value);
      return null;
    } catch {
      return null;
    }
  };

  const fmtDateTime = (v) => {
    const d = coerceDate(v);
    return d ? d.toLocaleString() : '—';
  };

  const userStatus = (user) => {
    const now = Date.now();
    const suspendedUntil = coerceDate(user.suspendedUntil)?.getTime();
    if (suspendedUntil && suspendedUntil > now) return 'suspended';
    if (user.flagsCount && user.flagsCount > 0) return 'flagged';
    const lastActive = coerceDate(user.lastActive)?.getTime();
    if (!lastActive || lastActive < now - 30 * 86400000) return 'inactive';
    return 'active';
  };

  const getStatusBadge = (user) => {
    const status = userStatus(user);
    const palette = {
      active: '#d4edda',
      inactive: '#e0e0e0',
      flagged: '#fff3cd',
      suspended: '#ffcccb',
    };
    const label = {
      active: 'Active',
      inactive: 'Inactive',
      flagged: 'Flagged',
      suspended: 'Suspended',
    }[status];
    return <span style={{ ...styles.badge, backgroundColor: palette[status] }}>{label}</span>;
  };

  // --- Clipboard helper
  const copyToClipboard = async (text, note = 'Copied!') => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(text));
      } else {
        const ta = document.createElement('textarea');
        ta.value = String(text);
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setToast(note);
      setTimeout(() => setToast(null), 1500);
    } catch (e) {
      console.error('❌ Copy failed:', e);
      alert('Copy failed');
    }
  };

  // --- Actions
  const toggleVerified = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'users', userId), { isVerified: !currentStatus });
      await logAdminAction('toggle_verified', userId, { newStatus: !currentStatus });
    } catch (err) {
      console.error('❌ Toggle verified failed:', err);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Permanently delete this user?')) return;
    const reason = prompt('Reason for deletion:') || '';
    try {
      await deleteDoc(doc(db, 'users', userId));
      await logAdminAction('delete_user', userId, { reason });
    } catch (err) {
      console.error('❌ Delete failed:', err);
    }
  };

  const suspendUser = async (userId) => {
    const days = prompt('Suspend for how many days?');
    const reason = prompt('Reason for suspension:') || '';
    if (!days || isNaN(days)) return;
    const until = new Date();
    until.setDate(until.getDate() + parseInt(days, 10));
    try {
      await updateDoc(doc(db, 'users', userId), { suspendedUntil: until.toISOString() });
      await logAdminAction('suspend_user', userId, { reason, until: until.toISOString() });
      alert('✅ Suspended until ' + until.toDateString());
    } catch (err) {
      console.error('❌ Suspend failed:', err);
    }
  };

  const upgradeUserTier = async (userId, currentTier) => {
    const newTier = prompt('Enter new tier:', currentTier || 'basic');
    const reason = prompt('Reason for upgrade:') || '';
    if (!newTier || newTier === currentTier) return;
    try {
      await updateDoc(doc(db, 'users', userId), { subscriptionTier: newTier });
      await logAdminAction('upgrade_tier', userId, { oldTier: currentTier || 'basic', newTier, reason });
      alert('✅ Tier upgraded to ' + newTier);
    } catch (err) {
      console.error('❌ Tier upgrade failed:', err);
    }
  };

  const viewAuditLog = (userId) => navigate(`/audit-log?user=${userId}`);

  // --- Derived data (memoized)
  const filteredUsers = useMemo(() => {
    const q = searchQuery;
    return users
      .filter((u) => {
        // quick tier filter
        if (tierFilter !== 'all' && (u.subscriptionTier || 'basic').toLowerCase() !== tierFilter) {
          return false;
        }
        // quick status filter
        if (statusFilter !== 'all' && userStatus(u) !== statusFilter) {
          return false;
        }
        if (!q) return true;
        // safe text search
        const name = (u.name || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .sort((a, b) => {
        if (sortBy === 'createdDesc') {
          const ad = coerceDate(a.createdAt)?.getTime() || 0;
          const bd = coerceDate(b.createdAt)?.getTime() || 0;
          return bd - ad;
        }
        if (sortBy === 'nameAsc') {
          return (a.name || '').localeCompare(b.name || '');
        }
        // default lastActiveDesc
        const al = coerceDate(a.lastActive)?.getTime() || 0;
        const bl = coerceDate(b.lastActive)?.getTime() || 0;
        return bl - al;
      });
  }, [users, searchQuery, tierFilter, statusFilter, sortBy]);

  const counts = useMemo(() => {
    const total = users.length;
    const verified = users.filter((u) => !!u.isVerified).length;
    const pro = users.filter((u) => (u.subscriptionTier || 'basic').toLowerCase() === 'pro').length;
    const gold = users.filter((u) => (u.subscriptionTier || 'basic').toLowerCase() === 'gold').length;
    const suspended = users.filter((u) => userStatus(u) === 'suspended').length;
    return { total, verified, pro, gold, suspended };
  }, [users]);

  // --- CSV export (current filtered view)
  const exportCSV = () => {
    const header = [
      'id',
      'email',
      'name',
      'tier',
      'verified',
      'status',
      'createdAt',
      'lastActive',
      'flagsCount',
      'suspendedUntil',
      'city',
    ];
    const rows = filteredUsers.map((u) => [
      u.id,
      u.email || '',
      u.name || '',
      (u.subscriptionTier || 'basic'),
      u.isVerified ? 'yes' : 'no',
      userStatus(u),
      fmtDateTime(u.createdAt),
      fmtDateTime(u.lastActive),
      u.flagsCount ?? 0,
      u.suspendedUntil || '',
      u.city || '',
    ]);
    const csv = [header, ...rows].map((r) =>
      r
        .map((x) => {
          const s = String(x ?? '');
          return s.includes(',') ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>← Back</button>
      <div style={styles.headerRow}>
        <h2 style={styles.header}>👥 Users</h2>
        <div style={styles.headerActions}>
          <button onClick={exportCSV} style={styles.exportBtn}>⬇️ Export CSV</button>
        </div>
      </div>

      <div style={styles.summaryRow}>
        <SummaryChip label="Total" value={counts.total} />
        <SummaryChip label="Verified" value={counts.verified} color="#e8f5e9" />
        <SummaryChip label="Pro" value={counts.pro} color="#e3f2fd" />
        <SummaryChip label="Gold" value={counts.gold} color="#fff8e1" />
        <SummaryChip label="Suspended" value={counts.suspended} color="#ffebee" />
      </div>

      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Search name or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={styles.search}
        />

        <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} style={styles.select}>
          <option value="all">All tiers</option>
          <option value="basic">Basic</option>
          <option value="gold">Gold</option>
          <option value="pro">Pro</option>
        </select>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.select}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive (30d)</option>
          <option value="flagged">Flagged</option>
          <option value="suspended">Suspended</option>
        </select>

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={styles.select}>
          <option value="lastActiveDesc">Sort: Last Active</option>
          <option value="createdDesc">Sort: Newest</option>
          <option value="nameAsc">Sort: Name A→Z</option>
        </select>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading users…</div>
      ) : filteredUsers.length === 0 ? (
        <div style={styles.emptyState}>No users match your filters.</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Tier</th>
              <th style={styles.th}>Verified</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Last Active</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} onClick={() => setSelectedUser(user)} style={{ cursor: 'pointer' }}>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>{user.name || '—'}</td>
                <td style={styles.td}>{user.subscriptionTier || 'basic'}</td>
                <td style={styles.td}>
                  {user.isVerified ? '✅' : '❌'}
                  <button
                    style={styles.toggleButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleVerified(user.id, !!user.isVerified);
                    }}
                  >
                    Toggle
                  </button>
                </td>
                <td style={styles.td}>{getStatusBadge(user)}</td>
                <td style={styles.td}>{fmtDateTime(user.lastActive)}</td>
                <td style={styles.td}>
                  <div style={{ position: 'relative' }} ref={(el) => (dropdownRefs.current[user.id] = el)}>
                    <button
                      style={styles.moreButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(dropdownOpen === user.id ? null : user.id);
                      }}
                    >
                      ⋯
                    </button>
                    {dropdownOpen === user.id && (
                      <div style={styles.dropdown} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => upgradeUserTier(user.id, user.subscriptionTier)}>⭐ Upgrade</button>
                        <button onClick={() => suspendUser(user.id)}>⛔ Suspend</button>
                        <button onClick={() => alert('Flags: ' + (user.flagsCount || 0))}>🚩 Flags</button>
                        <button onClick={() => viewAuditLog(user.id)}>📜 Audit</button>
                        <button onClick={() => deleteUser(user.id)}>🗑️ Delete</button>
                        <hr style={{ margin: '0.4rem 0', border: 0, borderTop: '1px solid #eee' }} />
                        <button
                          onClick={() => copyToClipboard(user.id, 'UID copied')}
                          title="Copy UID"
                        >
                          📋 Copy UID
                        </button>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              user.stripeCustomerId || '(none)',
                              'Stripe Customer ID copied'
                            )
                          }
                          title="Copy Stripe Customer ID"
                        >
                          💳 Copy Stripe Customer ID
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedUser && (
        <div style={styles.modalOverlay} onClick={() => setSelectedUser(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>👤 {selectedUser.name || 'Unnamed'}</h3>
            <p><strong>Email:</strong> {selectedUser.email || '—'}</p>
            <p><strong>Tier:</strong> {selectedUser.subscriptionTier || 'basic'}</p>
            <p><strong>Verified:</strong> {selectedUser.isVerified ? 'Yes ✅' : 'No ❌'}</p>
            <p><strong>City:</strong> {selectedUser.city || 'N/A'}</p>
            <p><strong>Status:</strong> {userStatus(selectedUser)}</p>
            <p><strong>Signed Up:</strong> {fmtDateTime(selectedUser.createdAt)}</p>
            <p><strong>Last Active:</strong> {fmtDateTime(selectedUser.lastActive)}</p>

            {/* Quick copy row in modal too */}
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                style={styles.smallCopyBtn}
                onClick={() => copyToClipboard(selectedUser.id, 'UID copied')}
              >
                📋 Copy UID
              </button>
              <button
                style={styles.smallCopyBtn}
                onClick={() =>
                  copyToClipboard(selectedUser.stripeCustomerId || '(none)', 'Stripe Customer ID copied')
                }
              >
                💳 Copy Stripe Customer ID
              </button>
            </div>

            <button onClick={() => setSelectedUser(null)} style={styles.closeBtn}>Close</button>
          </div>
        </div>
      )}

      {/* Tiny toast */}
      {toast && (
        <div style={styles.toast} role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}

// --- Tiny presentational chip
function SummaryChip({ label, value, color = '#f5f5f5' }) {
  return (
    <div style={{ ...styles.summaryChip, backgroundColor: color }}>
      <div style={{ fontSize: 12, color: '#555' }}>{label}</div>
      <div style={{ fontWeight: 700 }}>{value}</div>
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
    position: 'relative',
  },
  backButton: {
    marginBottom: '1rem',
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    justifyContent: 'space-between',
  },
  header: {
    fontSize: '1.5rem',
    color: '#065f46',
  },
  headerActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  exportBtn: {
    backgroundColor: '#0ea5e9',
    color: 'white',
    padding: '0.5rem 0.75rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  summaryRow: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    margin: '1rem 0',
  },
  summaryChip: {
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    minWidth: 90,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  toolbar: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '1rem',
  },
  search: {
    padding: '0.5rem 1rem',
    width: '100%',
    maxWidth: '320px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    background: '#fff',
  },
  select: {
    padding: '0.45rem 0.5rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    background: '#fff',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#555',
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#555',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
  },
  th: {
    borderBottom: '2px solid #ccc',
    padding: '0.75rem',
    textAlign: 'left',
    backgroundColor: '#e0f2f1',
  },
  td: {
    borderBottom: '1px solid #ddd',
    padding: '0.75rem',
    verticalAlign: 'middle',
  },
  toggleButton: {
    marginLeft: '0.5rem',
    padding: '0.3rem 0.6rem',
    fontSize: '0.8rem',
    border: '1px solid #4caf50',
    borderRadius: '4px',
    backgroundColor: '#e8f5e9',
    cursor: 'pointer',
  },
  moreButton: {
    fontSize: '1.2rem',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: '6px',
    padding: '0.5rem',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    minWidth: '210px',
  },
  badge: {
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    color: '#333',
    fontSize: '0.8rem',
    display: 'inline-block',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 50,
  },
  modal: {
    background: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    width: '420px',
    maxWidth: '95vw',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  closeBtn: {
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#eee',
    border: '1px solid #ccc',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  smallCopyBtn: {
    padding: '0.35rem 0.6rem',
    border: '1px solid #bbb',
    borderRadius: 6,
    background: '#f6f6f6',
    cursor: 'pointer',
    fontSize: 12,
  },
  toast: {
    position: 'fixed',
    right: 16,
    bottom: 16,
    background: '#111',
    color: '#fff',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 13,
    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
    zIndex: 100,
  },
};
